import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { isInGameSession, getGameSession } from '../services/casinoService'
import {
  getDefaultWithdrawalSettings,
  getWithdrawalSettings,
  type WithdrawalSettings,
} from '../services/withdrawalSettingsService'
import { createWithdrawal } from '../services/walletTransactionService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernWithdrawPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const translateWithdrawalErrorMessage = (errorMessage: string): string => {
  const normalizedMessage = String(errorMessage || '').trim()
  if (!normalizedMessage) {
    return 'เกิดข้อผิดพลาดในการส่งคำขอถอนเงิน'
  }

  const minimumMatch = normalizedMessage.match(/^Minimum withdrawal amount is\s+(\d+(?:\.\d+)?)$/i)
  if (minimumMatch) {
    return `ยอดถอนขั้นต่ำคือ ${walletService.formatCurrency(Number(minimumMatch[1]))} บาท`
  }

  const maximumMatch = normalizedMessage.match(/^Maximum withdrawal amount per transaction is\s+(\d+(?:\.\d+)?)$/i)
  if (maximumMatch) {
    return `ยอดถอนสูงสุดต่อครั้งคือ ${walletService.formatCurrency(Number(maximumMatch[1]))} บาท`
  }

  const dailyLimitMatch = normalizedMessage.match(/^Daily withdrawal request limit exceeded\s*\((\d+)\s+per day\)$/i)
  if (dailyLimitMatch) {
    return `คุณทำรายการถอนครบจำนวน ${dailyLimitMatch[1]} ครั้งต่อวันแล้ว`
  }

  const dailyAmountLimitMatch = normalizedMessage.match(/^Daily withdrawal amount limit exceeded\s*\((\d+(?:\.\d+)?)\s+per day\)$/i)
  if (dailyAmountLimitMatch) {
    return `ยอดถอนรวมต่อวันเกิน ${walletService.formatCurrency(Number(dailyAmountLimitMatch[1]))} บาทแล้ว`
  }

  if (/^HTTP(?:\s+error!?\s*status:)?\s*\d+/i.test(normalizedMessage) || /^Failed to fetch$/i.test(normalizedMessage)) {
    return 'ไม่สามารถเชื่อมต่อระบบถอนได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง'
  }

  const mappedMessages: Record<string, string> = {
    'Validation failed': 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง',
    'Tenant ID and User ID are required': 'ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    'Invalid amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Wallet not found': 'ไม่พบบัญชีกระเป๋าเงิน กรุณาติดต่อแอดมิน',
    'Withdrawal blocked because bonus turnover or bonus conditions are not complete': 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal blocked because cashback turnover is not complete': 'ยังมีเงื่อนไข cashback turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal requires bonus forfeiture before it can continue': 'รายการถอนนี้ต้องสละสิทธิ์โบนัสก่อนจึงจะดำเนินการต่อได้',
    'Insufficient wallet balance': 'ยอดเงินในกระเป๋าไม่เพียงพอ',
    'Invalid withdrawal amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Unable to load withdrawal settings': 'ไม่สามารถโหลดเงื่อนไขการถอนได้ กรุณาลองใหม่อีกครั้ง',
  }

  return mappedMessages[normalizedMessage] || normalizedMessage
}

const getWithdrawalApprovalStatusText = (
  amountNumber: number,
  withdrawalSettings: WithdrawalSettings
): string => {
  const autoApproveDisabled = withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false
  const maxAutoApprovalAmount = Number(withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount || 0)
  const exceedsAutoApprovalLimit =
    maxAutoApprovalAmount >= 0 && amountNumber > maxAutoApprovalAmount

  if (autoApproveDisabled) {
    return 'ระบบปิดการอนุมัติอัตโนมัติ และรายการนี้จะรอเจ้าหน้าที่อนุมัติ'
  }

  if (exceedsAutoApprovalLimit) {
    return `ยอดนี้เกิน ${walletService.formatCurrency(maxAutoApprovalAmount)} บาท และจะรอเจ้าหน้าที่อนุมัติ`
  }

  return 'อยู่ในเกณฑ์ดำเนินการอัตโนมัติ'
}

/** URL โลโก้ธนาคารจาก bankCode หรือ imageUrl */
function getBankLogoUrl(account: authService.UserBankAccount | null, getAssetUrl: (p: string) => string): string {
  if (!account) return getAssetUrl('/img/b/KBANK.png')
  if (account.imageUrl) return account.imageUrl
  const code = (account.bankCode || '').toUpperCase()
  return getAssetUrl(`/img/b/${code || 'KBANK'}.png`)
}

/** หน้าถอนเงิน — แปลงจาก withdraw.html: หัวข้อ, บัญชีธนาคารของผู้ใช้, ยอดคงเหลือ, ช่องจำนวน, ปุ่มยืนยัน */
const ModernWithdrawPage: React.FC<ModernWithdrawPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [bankCheckDone, setBankCheckDone] = useState(false)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [withdrawalSettings, setWithdrawalSettings] = useState<WithdrawalSettings>(getDefaultWithdrawalSettings())

  // ── Game session guard: ป้องกันถอนขณะเล่นเกม ──────────────────────────────
  const [inGameSession, setInGameSession] = useState(() => isInGameSession())

  // Re-check on localStorage changes (e.g. game exits in the same tab or another tab)
  useEffect(() => {
    const check = () => setInGameSession(isInGameSession())
    window.addEventListener('storage', check)
    // Poll every 2 s as safety net (same-tab changes don't fire 'storage')
    const interval = setInterval(check, 2000)
    return () => {
      window.removeEventListener('storage', check)
      clearInterval(interval)
    }
  }, [])

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const primaryBank = userBankAccounts[0] ?? null
  const bankLogoUrl = getBankLogoUrl(primaryBank, getAssetUrl)
  const availableBalance = walletData?.balance?.main || 0

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
    ]).then(([walletRes, summary]) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
    })
  }, [])

  useEffect(() => {
    let active = true

    const loadSettings = async () => {
      setIsLoadingSettings(true)
      const nextSettings = await getWithdrawalSettings()
      if (active) {
        setWithdrawalSettings(nextSettings)
        setIsLoadingSettings(false)
      }
    }

    loadSettings()

    const handleSettingsUpdate = () => {
      loadSettings()
    }

    window.addEventListener('deposit_channels_updated', handleSettingsUpdate)
    return () => {
      active = false
      window.removeEventListener('deposit_channels_updated', handleSettingsUpdate)
    }
  }, [])

  // ดึงบัญชีธนาคารของผู้ใช้; ถ้ายังไม่มีให้ไปกรอกที่ /member
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      setBankCheckDone(true)
      return
    }
    authService.fetchUserBankAccounts().then((list) => {
      setBankCheckDone(true)
      setUserBankAccounts(list ?? [])
      if (!list || list.length === 0) navigate('/member', { replace: true })
    })
  }, [navigate])

  const balanceText = walletData != null
    ? walletService.formatCurrency(availableBalance) + ' ฿'
    : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const userBankSummaryText = primaryBank
    ? `${primaryBank.accountNumber} | ${primaryBank.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'

  const withdrawEnabled = themeConfig.settings?.withdrawEnabled !== false

  if (!bankCheckDone) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5 c_tertiary">กำลังโหลด...</div>
          </div>
        </div>
      </div>
    )
  }

  // ── Block withdrawal while player is in a game session ──────────────────────
  if (inGameSession) {
    const session = getGameSession()
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="c_quinary text-center">
                <h3 className="c_tertiary font_md">ถอนเงิน</h3>
              </div>
              <div className="row mx__1 mt-3">
                <div className="col-12 px-2">
                  <div
                    className="light_sd bg_op_secondary align-items-center bdc_primary p-4 p-md-5 border_30 text-center"
                    style={{ border: '1px solid rgba(239,68,68,0.4)' }}
                  >
                    <div style={{ fontSize: 48, marginBottom: '0.75rem' }}>🎮</div>
                    <h5 className="c_tertiary font_md mb-2">ไม่สามารถถอนเงินขณะเล่นเกมได้</h5>
                    <p className="c_quinary mb-3" style={{ opacity: 0.8 }}>
                      {session
                        ? `คุณกำลังเล่น "${session.gameName}" — กรุณาออกจากเกมก่อน แล้วกลับมาถอนเงิน`
                        : 'กรุณาออกจากเกมก่อน แล้วกลับมาถอนเงิน'}
                    </p>
                    <button
                      type="button"
                      className="btn"
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: '#fff',
                        fontWeight: 700,
                        padding: '0.55rem 1.8rem',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        window.location.hash = '/lobby-casino'
                      }}
                    >
                      กลับไปหน้าเกม
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!withdrawEnabled) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="c_quinary text-center">
                <h3 className="c_tertiary font_md">ถอนเงิน</h3>
              </div>
              <div className="row mx__1 mt-3">
                <div className="col-12 px-2">
                  <div className="light_sd bg_op_secondary align-items-center bdc_primary p-4 p-md-5 border_30 text-center">
                    <h5 className="c_tertiary font_md mb-3">ปิดการถอนเงินชั่วคราว</h5>
                    <p className="c_quinary mb-0">
                      ขณะนี้ระบบปิดการถอนเงิน กรุณาติดต่อแอดมินหรือลองใหม่ในภายหลัง
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const accountName = primaryBank?.accountName ?? '–'
  const accountNumber = primaryBank?.accountNumber ?? '–'
  const bankName = primaryBank?.bankName ?? '–'

  const checkWithdrawalConditions = () => {
    const currentTime = new Date()
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
    const [shutdownHour, shutdownMinute] = withdrawalSettings.withdrawalBot.shutdownTime.split(':').map(Number)
    const [activationHour, activationMinute] = withdrawalSettings.withdrawalBot.activationTime.split(':').map(Number)
    const shutdownMinutes = shutdownHour * 60 + shutdownMinute
    const activationMinutes = activationHour * 60 + activationMinute

    let isBotActive = true
    if (shutdownMinutes > activationMinutes) {
      isBotActive = currentMinutes >= activationMinutes && currentMinutes < shutdownMinutes
    } else {
      isBotActive = currentMinutes >= activationMinutes || currentMinutes < shutdownMinutes
    }

    if (!isBotActive) {
      return {
        isValid: false,
        message: `ระบบถอนเงินปิดชั่วคราว เปิดให้บริการเวลา ${withdrawalSettings.withdrawalBot.shutdownTime} - ${withdrawalSettings.withdrawalBot.activationTime}`,
      }
    }

    return { isValid: true }
  }

  const handleSubmitWithdrawal = async () => {
    const amountNumber = Number(amount)

    if (!amount || !Number.isFinite(amountNumber) || amountNumber <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุจำนวนเงินที่ต้องการถอน', confirmButtonColor: '#0d6efd' })
      return
    }

    if (isLoadingSettings) {
      await Swal.fire({ icon: 'info', title: 'กำลังโหลดการตั้งค่า', text: 'กรุณารอสักครู่แล้วลองใหม่อีกครั้ง', confirmButtonColor: '#0d6efd' })
      return
    }

    if (!primaryBank) {
      await Swal.fire({ icon: 'warning', title: 'ยังไม่พบบัญชีธนาคาร', text: 'กรุณาเพิ่มบัญชีธนาคารก่อนทำรายการถอนเงิน', confirmButtonColor: '#0d6efd' })
      navigate('/member')
      return
    }

    if (amountNumber < withdrawalSettings.withdrawalConditions.minWithdrawalAmount) {
      await Swal.fire({
        icon: 'warning',
        title: 'จำนวนเงินไม่ถูกต้อง',
        text: `ถอนเงินขั้นต่ำ ${walletService.formatCurrency(withdrawalSettings.withdrawalConditions.minWithdrawalAmount)} บาท`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    if (
      withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction > 0 &&
      amountNumber > withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดถอนเกินกำหนด',
        text: `ถอนได้ไม่เกิน ${walletService.formatCurrency(withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction)} บาท ต่อครั้ง`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    if (amountNumber > availableBalance) {
      await Swal.fire({
        icon: 'error',
        title: 'ยอดเงินไม่เพียงพอ',
        text: `ยอดเงินคงเหลือ ${walletService.formatCurrency(availableBalance)} บาท`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const conditionCheck = checkWithdrawalConditions()
    if (!conditionCheck.isValid) {
      await Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถถอนเงินได้',
        text: conditionCheck.message,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const requiresManualApproval =
      withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false ||
      (
        withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount >= 0 &&
        amountNumber > withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount
      )

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการถอนเงิน',
      html: `
        <div style="text-align:left">
          <p><strong>จำนวนเงิน:</strong> ${walletService.formatCurrency(amountNumber)} บาท</p>
          <p><strong>ธนาคาร:</strong> ${bankName}</p>
          <p><strong>เลขบัญชี:</strong> ${accountNumber}</p>
          <p><strong>ชื่อบัญชี:</strong> ${accountName}</p>
          <p><strong>สถานะ:</strong> ${getWithdrawalApprovalStatusText(amountNumber, withdrawalSettings)}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยันถอนเงิน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6b7280',
    })

    if (!confirmed.isConfirmed) {
      return
    }

    setIsSubmitting(true)
    try {
      const response = await createWithdrawal({
        amount: amountNumber,
        bankAccount: {
          bankCode: primaryBank.bankCode,
          accountNumber: primaryBank.accountNumber,
          accountName: primaryBank.accountName,
        },
        reason: 'user_withdrawal_request',
      })

      if (!response.success) {
        if (response.data?.bonuses?.length) {
          const bonusItems = response.data.bonuses
            .map((bonus) => {
              const remaining = Number(bonus.wageringRemaining || 0)
              const amount = Number(bonus.amount || 0)
              const note = String(bonus.note || '').trim()
              return `<li style="margin-bottom:8px;"><strong>${bonus.type || 'โบนัส'}</strong> วงเงิน ${walletService.formatCurrency(amount)} บาท, turnover คงเหลือ ${walletService.formatCurrency(remaining)} บาท${note ? `<br/><span style="color:#6b7280;">${note}</span>` : ''}</li>`
            })
            .join('')

          await Swal.fire({
            icon: 'warning',
            title: 'ยังถอนเงินไม่ได้',
            html: `
              <div style="text-align:left">
                <p>${translateWithdrawalErrorMessage(response.message || response.error || 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน')}</p>
                <p style="margin-top:12px;"><strong>turnover คงเหลือรวม:</strong> ${walletService.formatCurrency(Number(response.data.totalRemainingWagering || 0))} บาท</p>
                <ul style="padding-left:18px;margin-top:12px;">${bonusItems}</ul>
              </div>
            `,
            confirmButtonColor: '#0d6efd',
          })
          return
        }

        if (response.code === 'WITHDRAW_REQUEST_COOLDOWN_ACTIVE') {
          throw new Error(
            response.data?.userMessage
            || 'คุณเพิ่งส่งคำขอถอนเงินไปแล้ว กรุณารอสักครู่แล้วลองใหม่อีกครั้ง'
          )
        }

        throw new Error(response.message || response.error || 'ไม่สามารถส่งคำขอถอนเงินได้')
      }

      const refreshedWallet = await walletService.getWallet()
      if (refreshedWallet.success && refreshedWallet.data) {
        setWalletData(refreshedWallet.data)
      }
      setAmount('')

      await Swal.fire({
        icon: 'success',
        title: 'ส่งคำขอถอนเงินสำเร็จ',
        text: requiresManualApproval
          ? `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งแล้วและกำลังรอเจ้าหน้าที่อนุมัติ`
          : `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งเรียบร้อยแล้ว`,
        confirmButtonColor: '#0d6efd',
      })

      navigate('/history/withdraw')
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'ถอนเงินไม่สำเร็จ',
        text: translateWithdrawalErrorMessage(error instanceof Error ? error.message : ''),
        confirmButtonColor: '#0d6efd',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">ถอนเงิน</h3>
            </div>

            <div className="row mx__1 mt-3" style={{ rowGap: '1rem' }}>
              <div className="col-12 px-2">
                <div className="light_sd bg_op_secondary align-items-center bdc_primary p-3 p-md-4 border_30">
                  <div className="box_cd mx-auto text-center c_tertiary">
                    <h6 className="">ยอดเครดิตของคุณ</h6>
                    <h1 className="font_md my-3 bg_secondary c_primary border_5">{balanceText}</h1>
                    <h5 className="c_quinary">
                      คะแนนของคุณ : <span className="c_tertiary font_md">{pointsText}</span>
                    </h5>
                    <h6 className="mx-auto" style={{ width: 'fit-content' }}>
                      <Link to="/points" className="c_warning txt_links_primary text-decoration-none">
                        <u>ดูคะแนนทั้งหมด</u>
                      </Link>
                    </h6>
                  </div>
                  <div className="line_btw mt-4 mb-3 mx-auto" />
                  <div className="row mx__1">
                    <div className="col-12 col-md-5 offset-md-1 px-1 mt-2">
                      <Link
                        to="/topup"
                        className="link_pmr box_row justify-content-center font_rg bg_secondary gap-3 border_30 ins_sd ft_sd w-100 text-decoration-none"
                      >
                        <h5 className="c_secondary mb-0">ฝากเงิน</h5>
                      </Link>
                    </div>
                    <div className="col-12 col-md-5 px-1 mt-2">
                      <span className="link_pmr box_row justify-content-center font_rg bg_primary gap-3 border_30 ins_sd ft_sd d-inline-flex w-100 text-decoration-none c_tertiary">
                        <h5 className="c_tertiary mb-0">ถอนเงิน</h5>
                      </span>
                    </div>
                  </div>
                  <h6 className="c_quinary text-center mt-4">ข้อมูลบัญชีของคุณ :</h6>
                  <h5 className="c_tertiary font_md text-center">{userBankSummaryText}</h5>
                </div>
              </div>

              <div className="col-12 col-md-6 px-2">
                <div className="bg_secondary px-1 py-2 border_30 d-grid gap-1 h-100">
                  <div className="mx-auto" style={{ width: '70%', maxWidth: 100 }}>
                    <img src={bankLogoUrl} alt={bankName} className="object-contain" style={{ width: '100%', maxWidth: 100 }} onError={(e) => { (e.target as HTMLImageElement).src = getAssetUrl('/img/b/KBANK.png') }} />
                  </div>
                  <div className="text-center">
                    <h6 className="">ธนาคาร</h6>
                    <h5 className="font_md">{bankName}</h5>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 px-2">
                <div className="bg_op_secondary align-items-center px-1 py-2 border_30 d-grid gap-3 h-100">
                  <div className="text-center c_tertiary">
                    <h6 className="">ชื่อบัญชี</h6>
                    <h5 className="font_md">{accountName}</h5>
                  </div>
                  <div className="text-center c_tertiary">
                    <h6 className="">เลขบัญชี</h6>
                    <h5 className="font_md">{accountNumber}</h5>
                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="light_sd bg_gd_primary align-items-center bdc_primary px-1 py-2 border_30 d-grid gap-3">
                  <div className="text-center c_tertiary">
                    <h5 className="bg_gd_secondary c_tertiary rounded-1 text-center mt-2 px-2 mx-auto font_md" style={{ width: 'fit-content' }}>
                      ยอดเงินคงเหลือ
                    </h5>
                    <h1 className="font_md ft_sd">{balanceText}</h1>
                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="border_30 bg_secondary p-3 overflow-hidden">
                  <div className="bg_secondary box_row flex-nowrap border_30 px-2 py-1 bx_sd overflow-hidden w-100">
                    <h3 className="w-100 mb-0">
                      <input
                        type="text"
                        placeholder="จำนวนเงินที่ต้องการถอน"
                        className="w-100 border-0 px-2 text-center"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
                      />
                    </h3>
                  </div>
                  <h5 className="mt-3 c_primary text-center">
                    {isLoadingSettings
                      ? 'กำลังโหลดเงื่อนไขการถอน...'
                      : `ถอนขั้นต่ำ ${walletService.formatCurrency(withdrawalSettings.withdrawalConditions.minWithdrawalAmount)} บาท • สูงสุด ${walletService.formatCurrency(withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction)} บาท / ครั้ง`}
                  </h5>
                  {!isLoadingSettings && (
                    <h6 className="mt-2 c_quinary text-center">
                      เวลาปิดบริการถอน: {withdrawalSettings.withdrawalBot.shutdownTime} - {withdrawalSettings.withdrawalBot.activationTime}
                    </h6>
                  )}
                </div>
              </div>

              <div className="col-12 px-2">
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_5 w-100 border-0"
                  onClick={handleSubmitWithdrawal}
                  disabled={isSubmitting || isLoadingSettings || !primaryBank}
                >
                  <div className="d-flex align-items-center c_tertiary font_md">
                    <h5 className="mb-0">{isSubmitting ? 'กำลังส่งคำขอ...' : 'ยืนยันถอนเงินเข้าบัญชี'}</h5>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernWithdrawPage
